/**
 * Secure Firebase Configuration
 * OWASP A02: Cryptographic Failures Protection
 */

import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';
import { keyManager } from '../utils/keyManager';

class FirebaseConfig {
  private static instance: FirebaseConfig;
  private app: FirebaseApp | null = null;
  private auth: Auth | null = null;
  private db: Firestore | null = null;
  private storage: FirebaseStorage | null = null;
  private isInitialized = false;

  private constructor() {}

  public static getInstance(): FirebaseConfig {
    if (!FirebaseConfig.instance) {
      FirebaseConfig.instance = new FirebaseConfig();
    }
    return FirebaseConfig.instance;
  }

  /**
   * Initialize Firebase with secure configuration
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      console.log('🔥 Firebase already initialized');
      return;
    }

    try {
      // Get secure Firebase configuration
      const config = keyManager.getConfig('firebase');
      
      if (!config) {
        console.warn('⚠️ Firebase configuration not available');
        return;
      }

      // Validate configuration completeness
      if (!config.apiKey || !config.authDomain || !config.projectId) {
        throw new Error('Incomplete Firebase configuration');
      }

      // Initialize Firebase app
      this.app = initializeApp(config);
      
      // Initialize services
      this.auth = getAuth(this.app);
      this.db = getFirestore(this.app);
      this.storage = getStorage(this.app);

      this.isInitialized = true;
      console.log('🔥 Firebase initialized securely');

      // Run security audit
      this.auditConfiguration();

    } catch (error) {
      console.error('❌ Firebase initialization failed:', error);
      throw new Error('Firebase initialization failed');
    }
  }

  /**
   * Get Firebase Auth instance
   */
  public getAuth(): Auth {
    if (!this.auth) {
      throw new Error('Firebase Auth not initialized. Call initialize() first.');
    }
    return this.auth;
  }

  /**
   * Get Firestore instance
   */
  public getFirestore(): Firestore {
    if (!this.db) {
      throw new Error('Firestore not initialized. Call initialize() first.');
    }
    return this.db;
  }

  /**
   * Get Firebase Storage instance
   */
  public getStorage(): FirebaseStorage {
    if (!this.storage) {
      throw new Error('Firebase Storage not initialized. Call initialize() first.');
    }
    return this.storage;
  }

  /**
   * Get Firebase App instance
   */
  public getApp(): FirebaseApp {
    if (!this.app) {
      throw new Error('Firebase App not initialized. Call initialize() first.');
    }
    return this.app;
  }

  /**
   * Security audit for Firebase configuration
   */
  private auditConfiguration(): void {
    const issues: string[] = [];

    // Check if we're in production and configurations are secure
    if (process.env.NODE_ENV === 'production') {
      const config = keyManager.getConfig('firebase');
      
      if (config?.authDomain && !config.authDomain.includes('.firebaseapp.com')) {
        console.log('✅ Custom auth domain configured');
      }

      // Check if API key is properly obfuscated in production
      if (config?.apiKey && config.apiKey.length < 30) {
        issues.push('API key may not be properly secured');
      }
    }

    // Audit key manager
    const keyAudit = keyManager.auditSecurity();
    if (!keyAudit.passed) {
      issues.push(...keyAudit.issues);
    }

    if (issues.length > 0) {
      console.warn('⚠️ Security issues detected:', issues);
    } else {
      console.log('✅ Firebase security audit passed');
    }
  }

  /**
   * Check if Firebase is properly initialized
   */
  public isReady(): boolean {
    return this.isInitialized && this.app !== null;
  }

  /**
   * Cleanup Firebase resources
   */
  public cleanup(): void {
    this.auth = null;
    this.db = null;
    this.storage = null;
    this.app = null;
    this.isInitialized = false;
    console.log('🔥 Firebase resources cleaned up');
  }
}

// Export singleton instance
export const firebaseConfig = FirebaseConfig.getInstance();

// Auto-initialize Firebase
if (typeof window !== 'undefined') {
  firebaseConfig.initialize().catch(error => {
    console.warn('⚠️ Firebase auto-initialization failed:', error);
  });
}

export default firebaseConfig;